import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import axios from 'axios';

const TOKEN_KEY = 'knkt_aa_token';
const USER_KEY  = 'knkt_aa_user';

const API_BASE_URL = (() => {
  const url = import.meta.env.VITE_API_URL as string | undefined;
  if (url) return url;
  if (import.meta.env.PROD) {
    throw new Error('VITE_API_URL is required for production builds');
  }
  return 'http://localhost:8083';
})();
const AUTH_BASE   = `${API_BASE_URL}/api/auth`;

export interface KnktUser {
  knktId?: string;
  artistId?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  account?: string;
  admin?: boolean;
  profileImage?: string;
}

interface AuthContextValue {
  user: KnktUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<KnktUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Mount-time bootstrap. If the URL carries a KNKT SSO token (set by
  // knkt-local's Tools tab when iframing this app), exchange it for an
  // Audio Agent token first; otherwise restore the cached session.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('token') || params.get('sso');

    const restore = async () => {
      const t = localStorage.getItem(TOKEN_KEY);
      const u = localStorage.getItem(USER_KEY);
      if (!t || !u) { setLoading(false); return; }
      try {
        await axios.get(`${AUTH_BASE}/validate`, { headers: { Authorization: `Bearer ${t}` } });
        setToken(t);
        try { setUser(JSON.parse(u)); } catch { setUser(null); }
      } catch {
        // Backend rejects token (expired / invalid) — clear and let the
        // user sign in again. Tolerates the API being offline.
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      } finally {
        setLoading(false);
      }
    };

    const exchange = async (incoming: string) => {
      try {
        const { data } = await axios.post<{ token: string; user: KnktUser }>(
          `${AUTH_BASE}/sso`,
          { token: incoming },
        );
        if (data?.token && data?.user) {
          localStorage.setItem(TOKEN_KEY, data.token);
          localStorage.setItem(USER_KEY,  JSON.stringify(data.user));
          setToken(data.token);
          setUser(data.user);
        }
      } catch (e) {
        console.error('[AuthContext] SSO exchange failed', e);
      } finally {
        // Strip the SSO param from the URL so it doesn't sit in history
        params.delete('token');
        params.delete('sso');
        const search = params.toString();
        const next = window.location.pathname + (search ? `?${search}` : '');
        window.history.replaceState({}, '', next);
        setLoading(false);
      }
    };

    if (ssoToken) exchange(ssoToken);
    else restore();
  }, []);

  const login = async (email: string, password: string) => {
    const { data } = await axios.post<{ token: string; user: KnktUser }>(
      `${AUTH_BASE}/login`,
      { email, password },
    );
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY,  JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Reads token from storage so non-React code (axios instance) stays auth-aware. */
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
